/*******************************************************************************
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2015, 2024. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 *******************************************************************************/
'use strict';

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
var account = require('./account'),
  context = require('../shared/context')(),
  urls = require('./../shared/urls');

/*
 * An XHR helper that simplifies requests from the client.
 *
 * @param url The request url
 * @param options (optional) The request options, which include:
 *    method - (optional) The http method, default 'GET'
 *    headers - (optional) An object containing the headers to send, e.g. {'Content-Type': 'application/json'}
 *    dataType - (optional) The type of data to return: 'text' or 'json'. e.g. for json the response will be parsed
 *    body - (optional) A JSON-serializable object to send in the body, for PUT, POST
 * @param callback The callback, which will be called with an optional error param if there was an error, as well
 *    as the response body and raw xhr response.
 */
module.exports = function (url, options, callback) {
  url = addEnvId(url);
  // request json if dataType is json
  options = options || {};
  if (options.dataType === 'json') {
    if (!options.headers) {
      options.headers = {};
    }
    options.headers['Accept'] = 'application/json';
  }
  var xhr = new XMLHttpRequest();
  xhr.open(options.method || 'GET', url);
  if (options.headers) {
    for (var header in options.headers) {
      if (options.headers.hasOwnProperty(header)) {
        xhr.setRequestHeader(header, options.headers[header]);
      }
    }
  }
  // for security and server-side request handling (e.g. handle auth differently for XHRs)
  xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
  if (options.body) {
    xhr.setRequestHeader('Content-Type', 'application/json');
  }
  // propagate the account guid to ensure that IAM tokens are account-bound
  var acct = account.get();
  var accountGuid = acct === null || acct === void 0 ? void 0 : acct.accountGuid;
  if (accountGuid) {
    xhr.setRequestHeader('X-BSS-Account', accountGuid);
  }
  var contextId = acct === null || acct === void 0 ? void 0 : acct.contextId;
  if (contextId) {
    xhr.setRequestHeader('IBM-Account-Context', contextId);
  }
  // add csrf token
  xhr.setRequestHeader('IBM-CSRF-Token', context.csrfToken);
  xhr.onload = function () {
    var contentType = xhr.getResponseHeader('Content-Type'),
      result = xhr.responseText;
    if (xhr.status < 200 || xhr.status >= 300) {
      // tack on the response body message to the error object, either as plain text
      // or as JSON in the form { "description": "..." }
      if (options.dataType === 'json' && contentType && contentType.indexOf('application/json') === 0) {
        try {
          result = JSON.parse(result);
        } catch (e) {
          // report the error status code, not the problem parsing the json
        }
      }
      var error = new Error('Error ' + xhr.status),
        description = _typeof(result) === 'object' ? result.description : result,
        title = _typeof(result) === 'object' ? result.title : result;
      if (typeof description === 'string') {
        error.description = description;
      }
      if (typeof title === 'string') {
        error.title = title;
      }
      callback(error, result, xhr);
      return;
    }
    if (options.dataType === 'json' && contentType && contentType.indexOf('application/json') === 0) {
      try {
        result = JSON.parse(result);
      } catch (e) {
        callback(new Error('Error parsing response JSON: ' + result), result, xhr);
        return;
      }
    }
    callback(null, result, xhr);
  };
  xhr.onerror = function (error) {
    callback(error, null, xhr);
  };
  xhr.send(options.body ? JSON.stringify(options.body) : null);
  return xhr;
};

/*
 * Adds the env_id param to the URL for global console, based on whether it's specified in the browser URL.
 */
function addEnvId(url) {
  var envId = urls.param(location.href, 'env_id');
  if (envId && !urls.param(url, 'env_id')) {
    return urls.addUrlParam(url, 'env_id', envId);
  }
  return url;
}